import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';


const amountFormatter = (amount) => {
	let formatted;
	if (amount >= 1e7) { // 1 Cr
		formatted = (amount / 1e7);
		return (formatted % 1 === 0 ? formatted : formatted?.toFixed(2)) + ' Cr';
	} else if (amount >= 1e5) { // 1 Lakh
		formatted = (amount / 1e5);
		return (formatted % 1 === 0 ? formatted : formatted?.toFixed(2)) + ' L';
	} else if (amount >= 1e3) { // 1 Thousand
		formatted = (amount / 1e3);
		return (formatted % 1 === 0 ? formatted : formatted?.toFixed(2)) + ' K';
	} else {
		return (amount % 1 === 0 ? amount : amount?.toFixed(2));
	}
};

export const SIPPieChart = ({ totalInvestment, gainLoss }) => {
	const chartRef = useRef(null);
	const chartInstanceRef = useRef(null); 

	useEffect(() => {
		if (chartRef.current) {
			// initialize chart
			chartInstanceRef.current = echarts.init(chartRef.current);

			const option = {
				legend: { show: false },
				series: [{
					name: 'SIP Overview',
					type: 'pie',
					radius: ['50%', '70%'], // donut
					avoidLabelOverlap: false,
					label: {
						show: true,
						position: 'outside',
						formatter: '{d}%',
						distance: 0.1
					},
					labelLine: {
						show: false,
						length: 0.1
					},
					padAngle: 2,
					itemStyle: {
						borderRadius: 13
					},
					data: [
						{ value: totalInvestment, name: 'Total Investment' },
						{ value: gainLoss, name: 'Total Returns' }
					]
				}],
				color: ['#5B31A1', '#3dc269'] // Investment = Blue, Gain = Green
			};
			chartInstanceRef.current.setOption(option);
			// resize on window resize
			const handleResize = () => chartInstanceRef.current.resize();
			window.addEventListener('resize', handleResize);
			return () => {
				window.removeEventListener('resize', handleResize);
				chartInstanceRef.current.dispose();
			};
		}
	}, [totalInvestment, gainLoss]);
	return <div ref={chartRef} style={{ height: 270, width: '100%' }} />;
};

export const SIPLineChart = ({ tableData, frequency }) => {
	const chartRef = useRef(null);

	useEffect(() => {
		if (!chartRef.current || !tableData?.length) return;

		const chartInstance = echarts.init(chartRef.current);

		/* ------------------------------
		FLATTEN MONTH-WISE DATA
		--------------------------------*/
		const monthsData = tableData.flatMap(y => y.periods);

		// const xAxisData = monthsData.map((_, i) => i + 1);

		const investedSeries = monthsData.map(m => m.investedAmount);
		const gainLossSeries = monthsData.map(m => m.gainLoss);
		const maturitySeries = monthsData.map(m => m.maturityValue);

		// const xAxisData = monthsData.map((_, i) => i + 1);

		// const totalMonths = monthsData.length;
		// const totalYears = totalMonths / 12;

		const totalMonths = monthsData.length;
		const totalYears = totalMonths / 12;

		// ~6 labels (like before)
		const labelStep = 12 * Math.ceil(totalYears / 6);

		/* ------------------------------
		CHART OPTION
		--------------------------------*/
		const option = {
			tooltip: {
				trigger: 'axis',
				backgroundColor: '#fff',
				borderRadius: 8,
				borderWidth: 0,
				textStyle: {
					color: '#333',
					fontSize: 12
				},
				formatter: (params) => {
					const getValue = (name) =>
						params.find(p => p.seriesName === name)?.value ?? 0;

					const maturity = getValue('Maturity Value');
					const invested = getValue('Invested Amount');
					const returns = getValue('Returns');

					return `
						<div style="
							padding:3px 3px;
							min-width:90px;'
						">
							<div style="color:#2F6BFF;font-weight:800;margin-bottom:4px;text-align:center">
								₹${amountFormatter(maturity)}
							</div>

							<div style="color:#4CAF50;font-weight:800;margin-bottom:4px;text-align:center">
								₹${amountFormatter(invested)}
							</div>

							<div style="color:#9C27B0;font-weight:800;text-align:center">
								₹${amountFormatter(returns)}
							</div>
						</div>
					`;
				}
			},
			grid: {
				top: 20,
				left: 40,
				right: 20,
				bottom: 40
			},

			// xAxis: {
			// 	type: 'category',
			// 	boundaryGap: false,
			// 	data: xAxisData,
			// 	axisLabel: {
			// 		formatter: '',
			// 		fontSize: 0
			// 	},
			// 	axisTick: { show: false },
			// 	axisLine: { show: false },
			// 	splitLine: {
			// 		show: true,
			// 		interval: (index) => (index + 1) % 12 === 0,
			// 		lineStyle: {
			// 			type: 'dashed',
			// 			color: '#cfd8dc'
			// 		}
			// 	},
			// 	name: `Frequency (${frequency})`,
			// 	nameLocation: 'middle',
			// 	nameGap: 20,
			// 	nameTextStyle: {
			// 		color: '#5b31a1',
			// 		fontSize: 13,
			// 		fontWeight: 500
			// 	}
			// },

			// yAxis: {
			// 	type: 'value',
			// 	// interval: 20000,
			// 	axisLabel: {
			// 		formatter: amountFormatter
			// 	},
			// 	axisLine: { show: false },
			// 	axisTick: { show: false },
			// 	splitLine: {
			// 		lineStyle: {
			// 			type: 'dashed',
			// 			color: '#cfd8dc'
			// 		}
			// 	}
			// },

			// xAxis: {
			// 	type: 'category',
			// 	boundaryGap: false,
			// 	data: monthsData.map((_, i) => i), // keep every data point

			// 	axisLabel: {
			// 		color: '#666',
			// 		fontSize: 12,

			// 		formatter: (value, index) => {
			// 			const year = Math.floor(index / 12);

			// 			// show only every 5th year
			// 			if (index % (12 * Math.ceil(totalYears / 6)) === 0) {
			// 				return `${year}Y`;
			// 			}
			// 			return '';
			// 		}
			// 	},

			// 	axisTick: { show: false },
			// 	axisLine: { show: false },

			// 	splitLine: {
			// 		show: true,
			// 		interval: (index) => index % 12 === 0, // vertical yearly line
			// 		lineStyle: {
			// 			type: 'dashed',
			// 			color: '#e0e0e0'
			// 		}
			// 	},

			// 	name: `Frequency (${frequency})`,
			// 	nameLocation: 'middle',
			// 	nameGap: 25,
			// 	nameTextStyle: {
			// 		color: '#5b31a1',
			// 		fontSize: 13,
			// 		fontWeight: 500
			// 	}
			// },

			xAxis: {
				type: 'category',
				boundaryGap: false,
				data: monthsData.map((_, i) => i),

				axisLabel: {
					// show: false
					color: '#666',
					fontSize: 12,

					formatter: (value, index) => {
						if (index % labelStep === 0) {
							// const year = Math.floor(index / 12);
							return '';
						}
						return '';
					}
				},

				axisTick: { show: false },
				axisLine: { show: false },

				// ⭐ THIS is the important fix
				splitLine: {
					show: true,
					interval: (index) => index % labelStep === 0,
					lineStyle: {
						type: 'dashed',
						color: '#e0e0e0',
						opacity: 0.6
					}
				},

				name: `Frequency (${frequency})`,
				nameLocation: 'middle',
				nameGap: 25,
				nameTextStyle: {
					color: '#5b31a1',
					fontSize: 13,
					fontWeight: 500
				}
			},

			yAxis: {
				type: 'value',
				splitNumber: 5, // 👈 max 6 segments
				min: 0, // optional (recommended for SIP charts)
				axisLabel: {
					formatter: amountFormatter
				},
				axisLine: { show: false },
				axisTick: { show: false },
				splitLine: {
					lineStyle: {
						type: 'dashed',
						color: '#cfd8dc'
					}
				}
			},

			series: [
				{
					name: 'Invested Amount',
					type: 'line',
					smooth: 0.6,
					symbol: 'circle',
					symbolSize: 5,
					data: investedSeries,
					lineStyle: { width: 1.5, color: '#4CAF50' },
					itemStyle: { color: '#4CAF50' },
					areaStyle: {
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color: 'rgba(76,175,80,0.3)' },
							{ offset: 1, color: 'rgba(76,175,80,0)' }
						])
					}
					
				},
				{
					name: 'Returns',
					type: 'line',
					smooth: 0.6,
					symbol: 'circle',
					symbolSize: 5,
					data: gainLossSeries,
					lineStyle: { width: 1.5, color: '#9C27B0' },
					itemStyle: { color: '#9C27B0' },
					areaStyle: {
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color: 'rgba(156,39,176,0.25)' },
							{ offset: 1, color: 'rgba(156,39,176,0)' }
						])
					}
				},
				{
					name: 'Maturity Value',
					type: 'line',
					smooth: 0.6,
					symbol: 'circle',
					symbolSize: 5,
					data: maturitySeries,
					lineStyle: { width: 1.5, color: '#2F6BFF' },
					itemStyle: { color: '#2F6BFF' },
					areaStyle: {
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color: 'rgba(47,107,255,0.35)' },
							{ offset: 1, color: 'rgba(47,107,255,0)' }
						])
					}
				}
			],
			animationDuration: 1500,
			animationEasing: 'cubicOut'
		};

		chartInstance.setOption(option);
		const handleResize = () => chartInstance.resize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			chartInstance.dispose();
		};
	}, [tableData, frequency]);

	return <div ref={chartRef} style={{ width: '100%', height: 400 }} />;
};
